import { type KeyboardEvent, type RefObject, useEffect, useId, useRef, useState } from 'react';
import { AdornedInput, Box, Spinner, Text } from '@zillow/constellation-10';
import { useTranslations } from 'next-intl';
import { focusOnAttach } from '@/src/components/ui/KeyboardProxyInput';
import { MobileAutocompleteModal } from '@/src/components/ui/MobileAutocomplete/MobileAutocompleteModal';
import { locationPillInput } from '@/src/components/ListingSearch/locationPill.styles';
import { getPlaceDetails, getPlacePredictions, type PlacePredictionOptions } from './googlePlaces';
import { PREDICTIONS_DEBOUNCE_MS } from './commuteAutocomplete.constants';
import type { CommuteDestination } from '@/src/lib/hooks/neighborhood';

type Prediction = Awaited<ReturnType<typeof getPlacePredictions>>[number];
type Props = {
  placeholder: string;
  predictionOptions?: PlacePredictionOptions;
  onSelect: (destination: CommuteDestination) => void;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLInputElement>;
};

export function CommuteSearchModal({ placeholder, predictionOptions, onSelect, onClose, returnFocusRef }: Props) {
  const t = useTranslations('hdp');
  const common = useTranslations('common');
  const listRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef(0);
  const selectingRef = useRef(false);
  const id = useId();
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState<Array<Prediction>>([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const request = ++requestRef.current;
    const timer = query.trim()
      ? setTimeout(async () => {
          const results = await getPlacePredictions(query.trim(), predictionOptions);
          if (request !== requestRef.current) return;
          setPredictions(results);
          setLoading(false);
        }, PREDICTIONS_DEBOUNCE_MS)
      : undefined;
    return () => {
      clearTimeout(timer);
      requestRef.current += 1;
    };
  }, [query, predictionOptions]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-option-index="${activeIndex}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const close = () => {
    requestRef.current += 1;
    onClose();
  };

  const select = async (prediction: Prediction) => {
    if (selectingRef.current) return;
    selectingRef.current = true;
    const request = ++requestRef.current;
    setResolving(true);
    setFailed(false);
    try {
      const destination = await getPlaceDetails(prediction.placeId, prediction.description);
      if (request !== requestRef.current) return;
      onSelect(destination);
    } catch {
      if (request !== requestRef.current) return;
      selectingRef.current = false;
      setResolving(false);
      setFailed(true);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      const prediction = predictions[activeIndex < 0 ? 0 : activeIndex];
      if (prediction && !event.repeat) void select(prediction);
    } else if (predictions.length && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      setActiveIndex((index) => {
        if (event.key === 'Home') return 0;
        if (event.key === 'End') return predictions.length - 1;
        if (event.key === 'ArrowDown') return (index + 1) % predictions.length;
        return index <= 0 ? predictions.length - 1 : index - 1;
      });
    }
  };

  return (
    <MobileAutocompleteModal
      title={t('searchForCommuteDestination')}
      returnFocusRef={returnFocusRef}
      onClose={close}
      input={
        <AdornedInput.Input
          ref={focusOnAttach}
          css={locationPillInput}
          id={`${id}-input`}
          type="search"
          autoComplete="off"
          role="combobox"
          tabIndex={0}
          aria-label={t('searchForCommuteDestination')}
          aria-expanded
          aria-controls={`${id}-results`}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${id}-${activeIndex}` : undefined}
          placeholder={placeholder}
          enterKeyHint="search"
          value={query}
          onKeyDown={handleKeyDown}
          onChange={(event) => {
            requestRef.current += 1;
            selectingRef.current = false;
            setQuery(event.target.value);
            setPredictions([]);
            setActiveIndex(-1);
            setLoading(Boolean(event.target.value.trim()));
            setResolving(false);
            setFailed(false);
          }}
        />
      }
    >
      <div
        ref={listRef}
        id={`${id}-results`}
        role="listbox"
        aria-label={t('searchForCommuteDestination')}
        aria-busy={loading || resolving}
      >
        {predictions.map((prediction, index) => (
          <Box
            asChild
            key={prediction.placeId}
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'tightest',
              width: '100%',
              padding: 'default',
              textAlign: 'start',
              cursor: 'pointer',
              backgroundColor: index === activeIndex ? 'bg.action.neutral.hover' : 'bg.neutral',
              _hover: { backgroundColor: 'bg.action.neutral.hover' },
            }}
          >
            <button
              type="button"
              role="option"
              aria-label={prediction.description}
              tabIndex={-1}
              id={`${id}-${index}`}
              aria-selected={index === activeIndex}
              disabled={resolving}
              data-option-index={index}
              onClick={() => void select(prediction)}
            >
              <Text asChild>
                <span>{prediction.primaryText}</span>
              </Text>
              <Text asChild textStyle="body-sm" color="text.subtle">
                <span>{prediction.secondaryText}</span>
              </Text>
            </button>
          </Box>
        ))}
      </div>
      {loading || resolving ? (
        <Box css={{ padding: 'default' }} role="status">
          <Spinner title={common('loading')} />
        </Box>
      ) : null}
      {failed ? (
        <Text role="alert" css={{ padding: 'default' }}>
          {t('commuteSearchError')}
        </Text>
      ) : null}
      {!loading && query.trim() && !predictions.length ? (
        <Text role="status" css={{ padding: 'default' }}>
          {common('noResults')}
        </Text>
      ) : null}
    </MobileAutocompleteModal>
  );
}
